import QRCodeStyling, {type DotType, type FileExtension, type Gradient, type Options} from 'qr-code-styling'
import {CORNER_DOT_BY_DOT_TYPE, CORNER_SQUARE_BY_DOT_TYPE} from '../constants/qrStyleOptions'

export interface QrGradientStop {
  id: string
  color: string
  position: number
}

export type QrColorMode = 'solid' | 'gradient'

export interface QrStyleState {
  dotType: DotType
  colorMode: QrColorMode
  color: string
  gradient: QrGradientStop[]
  gradientAngle: number
  bgTransparent: boolean
  bgColor: string
}

export interface QrLogoState {
  enabled: boolean
  /** null while enabled means "use the built-in Ecranify placeholder" */
  imageUrl: string | null
  size: number
  margin: number
}

export const DEFAULT_QR_LOGO_URL = '/logo-gradient.svg'
export const QR_EXPORT_SIZE_PX = 1000

const buildGradient = (stops: QrGradientStop[], angleDeg: number): Gradient => ({
  type: 'linear',
  rotation: (angleDeg * Math.PI) / 180,
  colorStops: [...stops].sort((a, b) => a.position - b.position).map((s) => ({offset: s.position, color: s.color})),
})

const resolveForegroundColor = (style: QrStyleState): string =>
  style.colorMode === 'gradient' ? (style.gradient[0]?.color ?? style.color) : style.color

export const buildQrOptions = (
  data: string,
  style: QrStyleState,
  logo: QrLogoState,
  sizePx: number = QR_EXPORT_SIZE_PX,
): Options => {
  const cornerColor = resolveForegroundColor(style)
  // The live preview reuses one QRCodeStyling instance across edits via
  // `.update()`, which deep-merges the new options onto the old ones instead
  // of replacing them — a key that's simply absent from the new object (e.g.
  // `gradient` while in solid mode) is NOT cleared, so a stale gradient from
  // a prior edit keeps rendering alongside the new solid `color`. Explicitly
  // null out the unused field so the merge actually overwrites it.
  const dotsOptions: Options['dotsOptions'] =
    style.colorMode === 'gradient'
      ? {type: style.dotType, gradient: buildGradient(style.gradient, style.gradientAngle), color: undefined}
      : {type: style.dotType, color: style.color, gradient: undefined}

  return {
    width: sizePx,
    height: sizePx,
    type: 'canvas',
    data,
    image: logo.enabled ? (logo.imageUrl ?? DEFAULT_QR_LOGO_URL) : undefined,
    margin: 16,
    qrOptions: {errorCorrectionLevel: logo.enabled ? 'H' : 'Q'},
    imageOptions: {hideBackgroundDots: true, imageSize: logo.size, margin: logo.margin, crossOrigin: 'anonymous'},
    dotsOptions,
    cornersSquareOptions: {type: CORNER_SQUARE_BY_DOT_TYPE[style.dotType], color: cornerColor},
    cornersDotOptions: {type: CORNER_DOT_BY_DOT_TYPE[style.dotType], color: cornerColor},
    backgroundOptions: {color: style.bgTransparent ? 'transparent' : style.bgColor},
  }
}

const isBlob = (value: Blob | Buffer | null): value is Blob => typeof Blob !== 'undefined' && value instanceof Blob

/** Renders a fresh, standalone QR code and returns its exported bytes as a Blob. */
export const generateQrBlob = async (
  data: string,
  style: QrStyleState,
  logo: QrLogoState,
  extension: FileExtension,
  sizePx: number = QR_EXPORT_SIZE_PX,
): Promise<Blob> => {
  const options = buildQrOptions(data, style, logo, sizePx)
  // JPEG has no alpha channel — swap a transparent background for white so the
  // export doesn't come back with an undefined/black fill.
  const backgroundOptions = extension === 'jpeg' && style.bgTransparent ? {color: '#ffffff'} : options.backgroundOptions

  const qr = new QRCodeStyling({...options, type: extension === 'svg' ? 'svg' : 'canvas', backgroundOptions})
  const raw = await qr.getRawData(extension)
  if (!isBlob(raw)) throw new Error('QR export failed')
  return raw
}
